/**
 * NextPayoutCard (Plan 20).
 *
 * Top-of-page card: total amount + cycle date + line breakdown by
 * kind. Per the plan's "Visual treatment" callout — DO NOT use big
 * green hero blocks. The card uses the neutral Card primitive with
 * `text-foreground` for the amount; color emphasis is reserved for
 * status chips, deltas, and the negative-total edge case
 * ("(Applied to next cycle)" hint).
 *
 * Edge cases handled:
 *   - No upcoming payout → empty-state copy.
 *   - All-CLAWBACK envelope (totalCents < 0) → destructive total +
 *     "(Applied to next cycle)" hint.
 *   - HELD envelope → status chip shows yellow tone + deep-links to
 *     the payment-blocking task inbox.
 *   - Each line kind subtotal is computed from the lines actually on
 *     the envelope; an absent kind doesn't render.
 */

import { Button } from '@ordo/ui/button'
import { Card } from '@ordo/ui/card'
import { cn } from '@ordo/ui/cn'

import type { KitchenPayoutDetail, PayoutStatus } from '@/api/billing-kitchen'

import { formatCents, formatCycleDateLong, formatPayoutPeriod, isNegativePayout } from './kitchen-payouts-helpers'
import { PayoutStatusChip } from './payout-status-chip'

export interface NextPayoutCardProps {
  payout: KitchenPayoutDetail | null
  /**
   * Opens the detail drawer for this envelope.
   *
   * The upcoming payout is the one a chef most wants itemised ("what am I
   * being paid on Friday, for what?"), and for a long time it was the ONLY
   * envelope with no route to a per-item view — history rows opened the drawer,
   * this card did not. The page already holds the fully-resolved lines for it,
   * so opening costs no extra fetch.
   */
  onViewDetail?: () => void
}

export function NextPayoutCard({ payout, onViewDetail }: NextPayoutCardProps) {
  if (!payout) {
    return <NoUpcomingPayoutCard />
  }
  return <PopulatedNextPayoutCard detail={payout} onViewDetail={onViewDetail} />
}

function NoUpcomingPayoutCard() {
  return (
    <Card className="p-6">
      <div className="flex flex-col gap-1">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Payout Total</p>
        <p className="text-sm text-muted-foreground">No upcoming payout</p>
        <p className="max-w-2xl text-xs text-muted-foreground">
          Your next payout will appear here when the next payment cycle is prepared.
        </p>
      </div>
    </Card>
  )
}

function PopulatedNextPayoutCard({ detail, onViewDetail }: { detail: KitchenPayoutDetail; onViewDetail?: () => void }) {
  const { payout, lines } = detail
  const totalIsNegative = isNegativePayout(payout)
  const period = formatPayoutPeriod(payout.periodStart, payout.periodEnd)
  const itemCount = lines.filter(
    (line) => (line.kind === 'ITEM' || line.kind === 'CARVE_OUT') && line.sourceOrderItemId,
  ).length
  return (
    <Card className="p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Payout Total</p>
          <p
            className={cn(
              'text-3xl font-semibold tabular-nums text-foreground',
              totalIsNegative && 'text-destructive',
            )}>
            {formatCents(payout.totalCents, payout.currency)}
          </p>
          <p className="text-sm text-muted-foreground">Scheduled {formatCycleDateLong(payout.cycleDate)}</p>
          {/* Which service days this pays for — the cycle date is only the upper bound. */}
          {period ? <p className="text-xs text-muted-foreground">Covers deliveries {period}</p> : null}
        </div>
        <PayoutStatusChip status={payout.status} title={null} />
      </div>

      {lines.some((line) => line.kind === 'VENDOR_SERVICE_FEE') ? (
        <div className="mt-4 flex justify-between text-sm">
          <span className="text-muted-foreground">Service fees</span>
          <span className="tabular-nums">
            {formatCents(
              lines
                .filter((line) => line.kind === 'VENDOR_SERVICE_FEE')
                .reduce((sum, line) => sum + line.amountCents, 0),
              payout.currency,
            )}
          </span>
        </div>
      ) : null}
      {onViewDetail && itemCount > 0 ? (
        <div className="mt-4 flex justify-start">
          <Button type="button" variant="outline" size="sm" onClick={onViewDetail}>
            View {itemCount.toLocaleString()} {itemCount === 1 ? 'item' : 'items'}
          </Button>
        </div>
      ) : null}
    </Card>
  )
}

// Type guard re-export so the page can call it without re-importing
// the helpers (kept here to mirror Plan 14's pattern of co-locating
// the typed prop next to its consumer).
export type { PayoutStatus }
